// LayoutEditorSlotContent.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './LayoutEditorSlotContent.css';

// Components
import CoverBlockValue from '../covervalue/CoverBlockValue';
import FieldBlockValue from '../fieldvalue/FieldBlockValue';
import PageNodePreview from '../PageNodePreview';
import PageVideoBlockValue from '../PageVideoBlockValue';
import RichTextEditor from 'components/richtext/RichTextEditor';
import RichTextRenderer from 'components/richtext/RichTextRenderer';
import PhotoView from 'nodetypes/photo/view/PhotoView';
import RollupSummaryCard from 'rollups/RollupSummaryCard';
import ViewRenderer from 'views/ViewRenderer';

/**
 * LayoutEditorSlotContent
 *
 * Renders the preview content inside a layout editor slot based on the current block type.
 *
 * @returns {ReactNode} The rendered slot preview content.
 */
const LayoutEditorSlotContent = ({
	blockPlaceholderLabel = null,
	discussionBlockTitle = '',
	fieldBlockRows = [],
	fieldRowUsePanelSurface = false,
	fieldsBlockTitle = '',
	contentZoneTitle = 'Content section',
	isContentZoneBlock = false,
	isCoverBlock = false,
	isDiscussionBlock = false,
	isFieldsBlock = false,
	isFieldsPreviewUnavailable = false,
	isNodeBlock = false,
	isNodeMissing = false,
	isRichTextBlock = false,
	isRichTextEmpty = false,
	isRollupBlock = false,
	isRollupMissing = false,
	isViewBlock = false,
	isVideoBlock = false,
	isVideoUploading = false,
	isViewEmpty = false,
	isViewMissing = false,
	isViewReady = false,
	isViewTypePending = false,
	inlineRichTextValue = null,
	isInlineRichTextEditing = false,
	inlineRichTextPlaceholder = 'Write page copy...',
	language = 'en',
	nodeBlockTitle = '',
	nodePresentation = null,
	isRichTextToolbarActive = false,
	onInlineRichTextBlur,
	onInlineRichTextChange,
	onRichTextMenuBarClick = null,
	onViewItemClick = null,
	pagePlaybackContext = null,
	resolvedNode = null,
	resolvedRollupResult = null,
	resolvedView = null,
	resolvedViewNodes = [],
	resolvedFieldsNode = null,
	richTextValue = null,
	rollupBlockTitle = '',
	rollupTypeLabel = '',
	typeLabelPlural = 'records',
	videoBlockTitle = '',
	videoUploadError = '',
	viewBlockTitle = '',
	viewBlockEnforceExplicitProperties = true,
	viewTypeDef = null,
}) => {

	// ----- CONTEXT ----- //

	const { theme } = useTheme();

	if (blockPlaceholderLabel) {
		return (
			<div className="layout-editor-slot-content-container">

				{/* BLOCK PLACEHOLDER */}
				<div
					className="layout-editor-block-placeholder"
				>
					{blockPlaceholderLabel}
				</div>
			</div>
		);
	}

	const containerClassName = [
		'layout-editor-slot-content-container',
		isRichTextBlock && !isRichTextEmpty ? 'layout-editor-slot-content-container-rich-text' : '',
	].filter(Boolean).join(' ');
	const isPhotoNodeBlock = isNodeBlock && String(resolvedNode?.type || '').trim().toUpperCase() === 'PHOTO';

	// ----- RENDER ----- //

	return (
		<div className={containerClassName}>
			{isViewBlock ? (
				<>

					{/* TITLE */}
					<div
						className="layout-editor-slot-content-title"
					>
						{viewBlockTitle}
					</div>

					{!isViewMissing ? (

						<div className="layout-editor-slot-content-surface">

							{/* EMPTY STATE */}
							{isViewReady ? (
								<ViewRenderer
									displayMode="compact"
									enforceExplicitProperties={viewBlockEnforceExplicitProperties}
									language={language}
									nodes={resolvedViewNodes}
									tableShowCheckboxes={false}
									typeDef={viewTypeDef}
									view={resolvedView}
									onItemClick={onViewItemClick}
								/>
							) : (
								<div
									className="layout-editor-slot-content-empty-state"
								>
									{isViewTypePending
										? 'Loading preview metadata...'
										: isViewEmpty
											? 'No related items match this view yet. They will appear here when attached to each page item.'
											: 'Preparing preview...'}
								</div>
							)}
						</div>
					) : null}
				</>
			) : isContentZoneBlock ? (
				<div
					className="layout-editor-slot-content-container-content-zone"
				>

					{/* TITLE */}
					<div
						className="layout-editor-slot-content-title"
					>
						{contentZoneTitle}
					</div>

					{/* EMPTY STATE */}
					<div className="layout-editor-slot-content-empty-state">
						Content in this section is unique to each {typeLabelPlural}. Edit it from a record&apos;s Pages tab.
					</div>
				</div>
			) : isFieldsBlock ? (
				<>

					{/* TITLE */}
					{fieldsBlockTitle ? (
						<div
							className="layout-editor-slot-content-title"
						>
							{fieldsBlockTitle}
						</div>
					) : null}

					{isFieldsPreviewUnavailable ? (
						<div
							className="layout-editor-slot-content-empty-state"
						>
							Field preview is unavailable for this layout type.
						</div>
					) : fieldBlockRows.length ? (

						<div className="layout-editor-slot-content-surface">

							{/* SURFACE FIELDS */}
							<div className="layout-editor-slot-content-fields-list">
								{fieldBlockRows.map((row) => (

									<div
										className={[
											'layout-editor-slot-content-fields-item',
											fieldRowUsePanelSurface ? 'layout-editor-slot-content-fields-item-panel' : '',
										].filter(Boolean).join(' ')}
										style={fieldRowUsePanelSurface ? {
											backgroundColor: theme.panel,
										} : undefined}
										key={row.fieldKey}
									>

										{/* FIELDS LABEL */}
										<div
											className="layout-editor-slot-content-fields-label"
										>
											{row.label}
										</div>

										{/* FIELDS VALUE */}
										<div
											className="layout-editor-slot-content-fields-value"
											style={{
												color: row.isEmpty ? theme.foregroundMuted : theme.foreground,
											}}
										>
											{row.isEmpty ? (
												<span className="layout-editor-slot-content-fields-empty-value">—</span>
											) : (
												<FieldBlockValue
													node={resolvedFieldsNode}
													row={row}
												/>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					) : (
						<div
							className="layout-editor-slot-content-empty-state"
						>
							No fields selected for this block yet.
						</div>
					)}
				</>
			) : isNodeBlock ? (
				<>

					{/* TITLE */}
					{!isPhotoNodeBlock ? (
						<div
							className="layout-editor-slot-content-title"
						>
							{nodeBlockTitle}
						</div>
					) : null}

					{/* SURFACE PHOTO */}
					{!isNodeMissing ? (
						<div
							className={[
								'layout-editor-slot-content-surface',
								isPhotoNodeBlock ? 'layout-editor-slot-content-surface-photo' : '',
							].filter(Boolean).join(' ')}
						>
							{isPhotoNodeBlock ? (
								<PhotoView node={resolvedNode} />
							) : (
								<PageNodePreview
									node={resolvedNode}
									presentation={nodePresentation}
								/>
							)}
						</div>
					) : null}
				</>
			) : isRichTextBlock ? (
				<>
					{isInlineRichTextEditing ? (

						<div className="layout-editor-slot-content-surface layout-editor-slot-content-surface-inline-rich-text">

							{/* RICH TEXT MENU BAR */}
							<button
								className={[
									'layout-editor-slot-content-rich-text-menu-bar',
									isRichTextToolbarActive ? 'is-active' : '',
								].filter(Boolean).join(' ')}
								style={{
									borderColor: theme.outlineMuted,
									color: theme.foregroundMuted,
								}}
								aria-label="Show block menu"
								title="Show block menu"
								type="button"
								onClick={onRichTextMenuBarClick}
								onMouseDown={(event) => event.preventDefault()}
							/>
							<RichTextEditor
								autoFocus
								placeholder={inlineRichTextPlaceholder}
								showInsertButton={false}
								stopInteractionPropagation
								toolbarPreset="article"
								value={inlineRichTextValue}
								onBlur={onInlineRichTextBlur}
								onChange={onInlineRichTextChange}
							/>
						</div>
					) : !isRichTextEmpty ? (

						<div className="layout-editor-slot-content-surface">
							<RichTextRenderer value={richTextValue} />
						</div>
					) : (
						<div
							className="layout-editor-slot-content-empty-state"
						>
							This text block is empty. Click or tap to add content.
						</div>
					)}
				</>
			) : isVideoBlock ? (

				<div className="layout-editor-slot-content-surface layout-editor-slot-content-surface-video">
					<PageVideoBlockValue
						blockTitle={videoBlockTitle}
						isUploading={isVideoUploading}
						node={resolvedNode}
						pagePlaybackContext={pagePlaybackContext}
						uploadError={videoUploadError}
					/>
				</div>
			) : isRollupBlock ? (
				<>

					{/* TITLE */}
					<div
						className="layout-editor-slot-content-title"
					>
						{rollupBlockTitle}
					</div>

					{!isRollupMissing ? (

						<div className="layout-editor-slot-content-surface layout-editor-slot-content-surface-rollup">
							<RollupSummaryCard
								labelScale={1.15}
								minWidth="100%"
								result={resolvedRollupResult}
								valueScale={1.65}
							/>
						</div>
					) : (
						<div
							className="layout-editor-slot-content-empty-state"
						>
							This rollup is missing. Choose another saved rollup from {rollupTypeLabel}.
						</div>
					)}
				</>
			) : isCoverBlock ? (
				<CoverBlockValue
					node={resolvedFieldsNode}
					showEmptyState
				/>
			) : isDiscussionBlock ? (
				<>

					{/* TITLE */}
					<div
						className="layout-editor-slot-content-title"
					>
						{discussionBlockTitle}
					</div>
					<div className="layout-editor-slot-content-empty-state">
						Discussion blocks were part of Pages and are no longer available.
					</div>
				</>
			) : (
				<div
					className="layout-editor-slot-content-empty"
				>
					---
				</div>
			)}
		</div>
	);
};

// Dev Tools
LayoutEditorSlotContent.displayName = 'LayoutEditorSlotContent';

export default LayoutEditorSlotContent;
